/**
 * Session/local storage persistence + MRT<->DataGrid model translation for the
 * table wrapper. Pure and side-effect-free apart from the storage reads/writes,
 * so the translation helpers can be unit-tested directly.
 *
 * Storage shape is persisted in DataGrid terms: { page, pageSize, quickFilter }.
 */

export const STORAGE_PREFIX = 'tbl_';
export const PREFERENCE_DEFAULTS_KEY = 'tablePreferenceDefaults';

export function getPreferenceDefaults() {
  try {
    const stored = localStorage.getItem(PREFERENCE_DEFAULTS_KEY);
    return stored ? JSON.parse(stored) : { rowsPerPage: 15, pageIndex: 0 };
  } catch {
    return { rowsPerPage: 15, pageIndex: 0 };
  }
}

export function setPreferredPageSize(pageSize) {
  try {
    const stored = localStorage.getItem(PREFERENCE_DEFAULTS_KEY);
    const prefs = stored ? JSON.parse(stored) : {};
    localStorage.setItem(
      PREFERENCE_DEFAULTS_KEY,
      JSON.stringify({ ...prefs, rowsPerPage: pageSize })
    );
  } catch {
    // ignore quota/availability errors
  }
}

export function getTableStateFor(tableKey) {
  if (!tableKey) return undefined;
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + tableKey);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

export function saveTableState(tableKey, state) {
  if (!tableKey) return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable
  }
}

/**
 * globalFilter (single "contains" phrase, MRT semantics) <-> DataGrid
 * quickFilterValues. We keep the whole phrase as ONE value so matching stays a
 * single substring test rather than DataGrid's default space-split AND.
 */
export function globalFilterToQuickFilterValues(globalFilter) {
  const trimmed = (globalFilter ?? '').trim();
  return trimmed.length ? [trimmed] : [];
}

export function quickFilterValuesToGlobalFilter(values) {
  if (!Array.isArray(values) || values.length === 0) return '';
  // Rejoin defensively in case something upstream split the phrase.
  return values.join(' ');
}

/** Build the initial pagination model from props + persisted/default state. */
export function resolveInitialPagination({
  savedState,
  defaults,
  propPage,
  propPageSize,
  isManualPagination
}) {
  const pageSize = (() => {
    if (isManualPagination && propPageSize !== undefined) return propPageSize;
    return savedState?.pageSize ?? defaults.rowsPerPage ?? propPageSize ?? 15;
  })();
  const page = (() => {
    if (propPage !== undefined) return propPage;
    return savedState?.page ?? defaults.pageIndex ?? 0;
  })();
  return { page, pageSize };
}

/**
 * Should a pagination change from the DataGrid be ignored as a spurious reset?
 *
 * When `rowCount` momentarily drops to 0 — which it does on a server-paginated
 * table whose row count comes from an in-flight request — the grid decides the
 * current page is out of range and "corrects" it by emitting page 0. Passed
 * through to a controlled parent, that silently teleports the user back to the
 * first page mid-browse.
 *
 * Only a transient empty state is suppressed: page 0 arriving while we are past
 * it, with no rows AND no row count. Once the response lands, the real page
 * renders from the state we kept. A genuinely empty result leaves the user on
 * their current page with an empty grid, which self-corrects on the next
 * interaction — the same trade the client-side path has always made.
 *
 * @returns {boolean} true when the change should be dropped
 */
export function isSpuriousPageReset({ next, prev, rowsLength, totalCount }) {
  return (
    next?.page === 0 &&
    prev?.page > 0 &&
    rowsLength === 0 &&
    (totalCount ?? 0) === 0
  );
}

/**
 * Clamp a restored page index to what the row count can actually support.
 *
 * A page restored from a previous session can point past the end of the data —
 * records get deleted, filters change, someone else edits. Left alone that shows
 * an empty grid under a footer claiming thousands of rows.
 *
 * Deliberately inert when `totalCount` is 0: that is the transient
 * request-in-flight state, and correcting the page there is precisely the bug
 * `isSpuriousPageReset` exists to suppress. Only a real, non-zero count clamps.
 *
 * @returns {number} the page to use — unchanged when already in range
 */
export function clampPageToTotal({ pageIndex, pageSize, totalCount }) {
  const page = Number(pageIndex) || 0;
  const size = Number(pageSize) || 0;
  const total = Number(totalCount) || 0;
  if (total <= 0 || size <= 0) return page;
  const lastPage = Math.max(0, Math.ceil(total / size) - 1);
  return Math.min(page, lastPage);
}
